import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { X, ZoomIn, ZoomOut, Maximize, Minimize, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export function Lightbox({ images = [], index = 0, title, subtitle, onClose }) {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [fs, setFs] = useState(false);
  const touch = useRef({ x: 0, y: 0, dist: 0, zoomStart: 1, ox: 0, oy: 0, panning: false });
  const drag = useRef({ on: false, x: 0, y: 0, ox: 0, oy: 0 });
  const containerRef = useRef(null);

  const clamp = (n) => Math.max(0, Math.min(images.length - 1, n));
  const reset = () => { setZoom(1); setOff({ x: 0, y: 0 }); };
  const go = useCallback((d) => { setI((c) => clamp(c + d)); reset(); }, [images.length]);
  const zoomBy = (d) => setZoom((z) => { const n = Math.max(1, Math.min(5, +(z + d).toFixed(2))); if (n === 1) setOff({ x: 0, y: 0 }); return n; });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); onClose(); }
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") zoomBy(0.5);
      else if (e.key === "-") zoomBy(-0.5);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [go, onClose]);

  const toggleFs = async () => {
    try {
      if (!document.fullscreenElement) { await containerRef.current?.requestFullscreen(); setFs(true); }
      else { await document.exitFullscreen(); setFs(false); }
    } catch { /* ignore */ }
  };
  useEffect(() => { const h = () => setFs(!!document.fullscreenElement); document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h); }, []);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) touch.current = { ...touch.current, dist: dist(e.touches), zoomStart: zoom };
    else if (e.touches.length === 1) { touch.current.x = e.touches[0].clientX; touch.current.y = e.touches[0].clientY; touch.current.ox = off.x; touch.current.oy = off.y; touch.current.panning = zoom > 1; }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && touch.current.dist) {
      const ratio = dist(e.touches) / touch.current.dist;
      setZoom(Math.max(1, Math.min(5, +(touch.current.zoomStart * ratio).toFixed(2))));
    } else if (e.touches.length === 1 && touch.current.panning) {
      setOff({ x: touch.current.ox + (e.touches[0].clientX - touch.current.x), y: touch.current.oy + (e.touches[0].clientY - touch.current.y) });
    }
  };
  const onTouchEnd = (e) => {
    if (zoom === 1 && !touch.current.panning && e.changedTouches.length === 1) {
      const dx = e.changedTouches[0].clientX - touch.current.x;
      if (dx > 60) go(-1); else if (dx < -60) go(1);
    }
    touch.current.dist = 0; touch.current.panning = false;
  };
  const onMouseDown = (e) => { if (zoom <= 1) return; drag.current = { on: true, x: e.clientX, y: e.clientY, ox: off.x, oy: off.y }; };
  const onMouseMove = (e) => { if (!drag.current.on) return; setOff({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) }); };
  const endDrag = () => { drag.current.on = false; };

  const src = images[i];
  const btn = "p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-white/10 rounded-lg text-white";
  const node = (
    <div ref={containerRef} className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" data-testid="lightbox" onMouseUp={endDrag} onMouseLeave={endDrag} onClick={onClose}>
      <div className="flex items-center justify-between p-3 text-white/90 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0"><div className="font-semibold text-sm truncate">{title}</div>{subtitle && <div className="text-xs text-white/60 truncate">{subtitle}</div>}{images.length > 1 && <div className="text-[11px] text-white/50">{i + 1} / {images.length}</div>}</div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => zoomBy(-0.5)} className={btn} aria-label="Zoom out" data-testid="lightbox-zoom-out"><ZoomOut className="w-5 h-5" /></button>
          <span className="text-xs w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={() => zoomBy(0.5)} className={btn} aria-label="Zoom in" data-testid="lightbox-zoom-in"><ZoomIn className="w-5 h-5" /></button>
          <button onClick={toggleFs} className={btn} aria-label="Full screen" data-testid="lightbox-fullscreen">{fs ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}</button>
          <button onClick={onClose} className={btn} aria-label="Close" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden select-none" onClick={(e) => e.stopPropagation()} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ touchAction: "none" }}>
        {images.length > 1 && <button onClick={() => go(-1)} disabled={i === 0} className="absolute left-2 z-10 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>}
        {src ? (
          <img src={src} alt={title || ""} draggable={false} className={cn("max-h-full max-w-full object-contain", zoom > 1 && "cursor-move")} style={{ transform: `translate(${off.x}px, ${off.y}px) scale(${zoom})` }} data-testid="lightbox-image" />
        ) : <div className="text-white/50">No image</div>}
        {images.length > 1 && <button onClick={() => go(1)} disabled={i === images.length - 1} className="absolute right-2 z-10 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto justify-center shrink-0" onClick={(e) => e.stopPropagation()}>
          {images.map((im, idx) => <img key={idx} src={im} alt="" onClick={() => { setI(idx); reset(); }} className={cn("w-14 h-14 rounded object-cover cursor-pointer shrink-0 opacity-60 hover:opacity-100", idx === i && "ring-2 ring-primary opacity-100")} />)}
        </div>
      )}
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}

export function Thumb({ src, onClick, testid, size = "w-12 h-12" }) {
  if (!src) return <div className={cn(size, "rounded bg-secondary flex items-center justify-center text-muted-foreground text-[10px]")} data-testid={testid ? `${testid}-placeholder` : undefined}>No photo</div>;
  return <button onClick={onClick} className={cn(size, "rounded overflow-hidden bg-secondary block group")} data-testid={testid}><img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></button>;
}
